import React, { useState, useEffect } from 'react';
import calendarApi from '../../services/calendarApi';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CalendarIcon,
  XMarkIcon,
  ArchiveBoxIcon,
  ArchiveBoxXMarkIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

const HolidayManagement = () => {
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [showArchived, setShowArchived] = useState(false);

  useEffect(() => {
    loadHolidays();
  }, [showArchived]);

  const loadHolidays = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await calendarApi.getHolidays(showArchived);
      setHolidays(response.data.holidays || []);
    } catch (err) {
      setError('Failed to load holidays: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (message, type = 'success') => {
    if (type === 'success') {
      setSuccess(message);
      setError('');
    } else {
      setError(message);
      setSuccess('');
    }
    setTimeout(() => {
      setSuccess('');
      setError('');
    }, 3000);
  };

  const handleAdd = () => {
    setEditingHoliday(null);
    setShowModal(true);
  };

  const handleEdit = (holiday) => {
    setEditingHoliday(holiday);
    setShowModal(true);
  };

  const handleDelete = async (holiday) => {
    if (!window.confirm(`Are you sure you want to delete "${holiday.name}"?`)) {
      return;
    }

    try {
      await calendarApi.deleteHoliday(holiday.id);
      showMessage('Holiday deleted successfully');
      loadHolidays();
    } catch (err) {
      showMessage('Failed to delete holiday: ' + err.message, 'error');
    }
  };

  const handleArchive = async (holiday) => {
    const action = holiday.is_archived ? 'unarchive' : 'archive';
    if (!window.confirm(`Are you sure you want to ${action} "${holiday.name}"?`)) {
      return;
    }

    try {
      await calendarApi.archiveHoliday(holiday.id, !holiday.is_archived);
      showMessage(`Holiday ${action}d successfully`);
      loadHolidays();
    } catch (err) {
      showMessage(`Failed to ${action} holiday: ` + err.message, 'error');
    }
  };

  const handleArchivePast = async () => {
    if (!window.confirm('This will archive all holidays that are more than 30 days in the past. Continue?')) {
      return;
    }

    try {
      const response = await calendarApi.archivePastHolidays();
      showMessage(response.message);
      loadHolidays();
    } catch (err) {
      showMessage('Failed to archive past holidays: ' + err.message, 'error');
    }
  };

  const handleSave = async (holidayData) => {
    try {
      if (editingHoliday) {
        await calendarApi.updateHoliday(editingHoliday.id, holidayData);
        showMessage('Holiday updated successfully');
      } else {
        await calendarApi.createHoliday(holidayData);
        showMessage('Holiday created successfully');
      }
      setShowModal(false);
      loadHolidays();
    } catch (err) {
      showMessage('Failed to save holiday: ' + err.message, 'error');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    
    try {
      const [year, month, day] = dateString.split('-');
      
      if (!year || !month || !day) {
        return 'Invalid date';
      }
      
      const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ];
      
      const monthIndex = parseInt(month) - 1;
      const dayNum = parseInt(day);
      
      if (monthIndex < 0 || monthIndex > 11 || dayNum < 1 || dayNum > 31) {
        return 'Invalid date';
      }
      
      const result = `${monthNames[monthIndex]} ${dayNum}, ${year}`;
      return result;
    } catch (e) {
      return 'Invalid date';
    }
  };

  const sortedHolidays = holidays.sort((a, b) => new Date(a.holiday_date) - new Date(b.holiday_date));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <CalendarIcon className="w-8 h-8 text-indigo-600" />
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Holiday Management</h2>
            <p className="text-sm text-gray-500">Manage company holidays and observances</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleArchivePast}
            className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 flex items-center gap-2"
            title="Archive holidays older than 30 days"
          >
            <ClockIcon className="w-4 h-4" />
            Archive Past
          </button>
          <button
            onClick={handleAdd}
            className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 flex items-center gap-2"
          >
            <PlusIcon className="w-4 h-4" />
            Add Holiday
          </button>
        </div>
      </div>

      {/* Archive Toggle */}
      <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
        <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
          />
          Show archived holidays only
        </label>
        <span className="text-sm text-gray-500">
          {showArchived 
            ? `Showing ${holidays.length} archived holidays`
            : `Showing ${holidays.length} active holidays`
          }
        </span>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md">
          {success}
        </div>
      )}

      {/* Holidays List */}
      <div className="bg-white rounded-lg shadow-sm">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
            <p className="mt-2 text-gray-500">Loading holidays...</p>
          </div>
        ) : sortedHolidays.length === 0 ? (
          <div className="p-8 text-center">
            <CalendarIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No holidays found</h3>
            <p className="text-gray-500 mb-4">
              {showArchived 
                ? 'No archived holidays found. Try unchecking "Show archived holidays only" to see active holidays.'
                : 'Get started by adding your first company holiday.'
              }
            </p>
            <button
              onClick={handleAdd}
              className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700"
            >
              Add Holiday
            </button>
          </div>
        ) : (
          <div className="overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Holiday Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created By
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedHolidays.map((holiday) => (
                  <tr key={holiday.id} className={`hover:bg-gray-50 ${holiday.is_archived ? 'bg-gray-50 opacity-75' : ''}`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <div className="text-sm font-medium text-gray-900">{holiday.name}</div>
                        {holiday.is_archived && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            <ArchiveBoxIcon className="w-3 h-3 mr-1" />
                            Archived
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {formatDate(holiday.holiday_date)}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 max-w-xs truncate">
                        {holiday.description || 'No description'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        holiday.is_archived 
                          ? 'bg-gray-100 text-gray-800' 
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {holiday.is_archived ? 'Archived' : 'Active'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {holiday.created_by_name || 'Unknown'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => handleEdit(holiday)}
                          className="text-indigo-600 hover:text-indigo-900 p-1"
                          title="Edit holiday"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleArchive(holiday)}
                          className={`p-1 ${holiday.is_archived ? 'text-green-600 hover:text-green-900' : 'text-yellow-600 hover:text-yellow-900'}`}
                          title={holiday.is_archived ? 'Unarchive holiday' : 'Archive holiday'}
                        >
                          {holiday.is_archived ? (
                            <ArchiveBoxXMarkIcon className="w-4 h-4" />
                          ) : (
                            <ArchiveBoxIcon className="w-4 h-4" />
                          )}
                        </button>
                        <button
                          onClick={() => handleDelete(holiday)}
                          className="text-red-600 hover:text-red-900 p-1"
                          title="Delete holiday"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Holiday Modal */}
      {showModal && (
        <HolidayModal
          holiday={editingHoliday}
          onClose={() => setShowModal(false)}
          onSave={handleSave}
        />
      )}
    </div>
  );
};

const HolidayModal = ({ holiday, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: holiday?.name || '',
    holiday_date: holiday?.holiday_date || '',
    description: holiday?.description || ''
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.holiday_date) {
      alert('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      await onSave(formData);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">
            {holiday ? 'Edit Holiday' : 'Add New Holiday'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Holiday Name *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="e.g., New Year's Day"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date *
            </label>
            <input
              type="date"
              name="holiday_date"
              value={formData.holiday_date}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="Optional description or notes about this holiday"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : (holiday ? 'Update Holiday' : 'Create Holiday')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default HolidayManagement; 